import { type Mesh, MeshBuilder, Vector3 } from "@babylonjs/core";
import { Btn, HeroId, Slot, ck, hv, pk } from "@rivalwatch/shared";
import { baked } from "../../audio/bake.ts";
import * as loops from "../../audio/cues/loops.ts";
import type { Cue } from "../../audio/engine.ts";
import type { Theme } from "../../audio/theory.ts";
import { bell, brass, choir, click, fm, later, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import { glow } from "../../game/materials.ts";
import type { ClientHero, ProjectileFrame } from "../types.ts";

const V = { bondA: 0, bondB: 1, bondC: 2, flight: 3, cocoonCd: 4 } as const;
const PK = { Cluster: pk(HeroId.AtomWarlock, 0) } as const;
const CK = { Cocoon: ck(HeroId.AtomWarlock, 0) } as const;
const hz = (semi: number, base = 293.66) => base * 2 ** (semi / 12);

interface Gear {
  halo: Mesh;
  browGem: Mesh;
  cape: Mesh;
  orbL: Mesh;
  orbR: Mesh;
  bonds: Mesh[];
}

const atomRay: Cue = baked(
  "fire:atom-warlock:ray-polish",
  () =>
    trim(7.7, (o) => {
      click(o, 0.22, 6200, 0.003);
      fm(o, { f: hz(28), ratio: 2.01, index: 1.3, index1: 0.2, gain: 0.14, d: 0.18 });
      tone(o, { f: hz(16), f1: hz(24), glide: 0.08, gain: 0.08, d: 0.18 });
      whoosh(o, 0.18, 900, 3600, 0.16);
      return 0.32;
    }),
  { variants: 5, jitter: 0.012 },
);

const atomCluster: Cue = baked(
  "fire:atom-warlock:cluster-polish",
  () =>
    trim(6.4, (o) => {
      for (let i = 0; i < 5; i++) fm(later(o, i * 0.018), { f: hz(19 + i), ratio: 2.7, index: 1.5, index1: 0.1, gain: 0.08, d: 0.18 });
      whoosh(o, 0.24, 1200, 4300, 0.22);
      click(o, 0.18, 5200, 0.004);
      return 0.38;
    }),
  { variants: 4, jitter: 0.012 },
);

const atomReload = (seconds: number): Cue =>
  baked("reload:atom-warlock:polish", () =>
    trim(10, (o) => {
      bell(o, hz(7), 0.22, 0.28);
      bell(later(o, seconds * 0.55), hz(14), 0.28, 0.32);
      fm(later(o, seconds * 0.82), { f: hz(21), ratio: 3, index: 0.9, gain: 0.08, d: 0.22 });
      return seconds + 0.25;
    }),
  );

const mendstream: Cue = trim(11.6, (o) => {
  bell(o, hz(12), 0.22, 0.55);
  tone(o, { f: hz(7), f1: hz(19), glide: 0.25, gain: 0.09, d: 0.35 });
  noise(o, { color: "pink", type: "bandpass", f: 1600, f1: 3600, q: 2, gain: 0.16, a: 0.04, d: 0.3 });
  return 0.75;
});

const soulStitch: Cue = trim(4.4, (o) => {
  [0, 7, 12].forEach((s, i) => choir(later(o, i * 0.045), hz(s), 0.35, 0.38));
  whoosh(o, 0.32, 500, 2400, 0.45);
  return 1;
});

const karmaReboot: Cue = trim(-3.3, (o) => {
  thump(o, 0.55, 80, 32, 0.55);
  for (const s of [-12, -5, 0, 7, 12]) brass(later(o, 0.08), hz(s), 0.8, 0.45);
  bell(later(o, 0.18), hz(30), 0.7, 0.55);
  return 1.6;
});

const atomTheme = {
  bpm: 84,
  octave: 0,
  voice: choir,
  double: (o, f, d, v) => void bell(o, f * 2, d, v * 0.45),
  notes: [
    [0, 1],
    [7, 1],
    [12, 1],
    [18, 2],
  ],
} satisfies Theme;

const hero: ClientHero<Gear> = {
  id: HeroId.AtomWarlock,
  bulk: 1.08,
  shape: { head: 1.05, torso: 1.08, arms: 0.98 },
  gear(g) {
    const { scene, R, H, rig, add, addWorld, glow, toon } = g;
    const halo = add(MeshBuilder.CreateTorus("atom-halo", { diameter: R * 1.8, thickness: 0.045, tessellation: 32 }, scene), rig.head, glow("#ffe58d"));
    halo.position.y = R * 1.35;
    halo.rotation.x = Math.PI / 2;
    const browGem = add(MeshBuilder.CreatePolyhedron("atom-forehead-gem", { type: 1, size: 0.09 }, scene), rig.head, glow("#fff0a6", 0.65));
    browGem.position.set(0, R * 0.22, -R * 0.5);
    const cape = add(MeshBuilder.CreateBox("atom-cape", { width: R * 1.55, height: H * 0.58, depth: 0.04 }, scene), rig.torso, toon("#5d1c0d"));
    cape.position.set(0, -H * 0.2, R * 0.62);
    cape.rotation.x = 0.08;
    const sash = add(MeshBuilder.CreateBox("atom-sash", { width: R * 0.32, height: H * 0.65, depth: 0.055 }, scene), rig.torso, toon("#ffe58d"));
    sash.position.set(-R * 0.25, H * 0.03, -R * 0.62);
    sash.rotation.z = -0.55;
    for (const side of [-1, 1]) {
      const cuff = add(MeshBuilder.CreateTorus(`atom-cuff-${side}`, { diameter: 0.28, thickness: 0.035, tessellation: 18 }, scene), side < 0 ? rig.handL : rig.handR, glow("#ffe58d", 0.65));
      cuff.rotation.x = Math.PI / 2;
      cuff.position.z = -0.05;
    }
    const orbL = add(MeshBuilder.CreateSphere("soul-orb-l", { diameter: 0.22, segments: 12 }, scene), rig.handL, glow("#fff4b8"));
    orbL.position.z = -0.18;
    const orbR = add(MeshBuilder.CreateSphere("soul-orb-r", { diameter: 0.22, segments: 12 }, scene), rig.handR, glow("#fff4b8"));
    orbR.position.z = -0.18;
    const bonds = [0, 1, 2].map((i) => addWorld(MeshBuilder.CreateBox(`bond-${i}`, { width: 0.045, height: 0.045, depth: 1 }, scene), glow("#ffe58d", 0.65)));
    return { halo, browGem, cape, orbL, orbR, bonds };
  },
  animate(gear, f) {
    gear.halo.rotation.z = f.t * 0.8;
    gear.browGem.rotation.y = f.t * 2;
    gear.cape.rotation.x = 0.08 + Math.sin(f.t * 2.3) * 0.025;
    gear.orbL.scaling.setAll(1 + Math.sin(f.t * 6) * 0.14);
    gear.orbR.scaling.setAll(1 + Math.cos(f.t * 6) * 0.14);
    const ids = [f.v[V.bondA], f.v[V.bondB], f.v[V.bondC]];
    for (let i = 0; i < gear.bonds.length; i++) {
      const b = gear.bonds[i]!;
      const ref = ids[i] ? f.lookup(ids[i]!) : undefined;
      b.setEnabled(!!ref && ref.alive);
      if (ref) {
        b.position.set((f.eye.x + ref.center.x) / 2, (f.eye.y + ref.center.y) / 2, (f.eye.z + ref.center.z) / 2);
        b.lookAt(new Vector3(ref.center.x, ref.center.y, ref.center.z));
        b.scaling.z = Math.hypot(ref.center.x - f.eye.x, ref.center.y - f.eye.y, ref.center.z - f.eye.z);
      }
    }
    return f.v[V.flight] > 0 ? "holding-both" : undefined;
  },
  hud(c, eid) {
    return [
      { label: "Cocoon", value: 1 - Math.min(1, hv(c, eid, V.cocoonCd) / 105), color: "#ffe58d" },
      { label: "Soul Bond", value: hv(c, eid, V.flight) > 0 ? 1 : 0, color: "#fff4b8" },
    ];
  },
  lifting(c, eid) {
    return hv(c, eid, V.flight) > 0;
  },
  constructs: {
    [CK.Cocoon]: (scene, frame) => {
      const shell = MeshBuilder.CreateSphere("atom-cocoon", { diameter: 1.2, segments: 16 }, scene);
      const ring = MeshBuilder.CreateTorus("atom-cocoon-ring", { diameter: 1.45, thickness: 0.035, tessellation: 32 }, scene);
      shell.material = glow(scene, "#ffe58d", 0.42);
      ring.material = glow(scene, "#fff0a6", 0.75);
      ring.parent = shell;
      shell.position.set(frame.pos.x, frame.pos.y + 0.65, frame.pos.z);
      return {
        gear: { shell, ring },
        update(g: { shell: Mesh; ring: Mesh }, f) {
          g.shell.position.set(f.pos.x, f.pos.y + 0.65, f.pos.z);
          g.ring.rotation.y += 0.04;
        },
        dispose: () => {
          ring.dispose();
          shell.dispose();
        },
      };
    },
  },
  projectiles: {
    [PK.Cluster]: (scene, frame: ProjectileFrame) => {
      const core = MeshBuilder.CreateSphere("atom-cluster-core", { diameter: 0.34, segments: 10 }, scene);
      const ring = MeshBuilder.CreateTorus("atom-cluster-ring", { diameter: 0.46, thickness: 0.022, tessellation: 20 }, scene);
      core.material = glow(scene, "#fff0a6", 0.9);
      ring.material = glow(scene, "#ffe58d", 0.75);
      ring.parent = core;
      core.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
      return {
        gear: { core, ring },
        update(g: { core: Mesh; ring: Mesh }, f) {
          g.core.position.set(f.pos.x, f.pos.y, f.pos.z);
          g.ring.rotation.x += f.dt * 8;
          g.ring.rotation.z += f.dt * 5;
        },
        dispose() {
          ring.dispose();
          core.dispose();
        },
      };
    },
  },
  icons: {
    [Slot.Primary]: "M4 12h16M12 4v16M7 7l10 10M17 7 7 17",
    [Slot.Secondary]: "M12 3c4 2 7 5 7 9s-3 7-7 9c-4-2-7-5-7-9s3-7 7-9zM8 12h8",
    [Slot.Ability1]: "M4 12c4-5 12-5 16 0c-4 5-12 5-16 0zM8 12h8",
    [Slot.Ability2]: "M5 14c4-6 10-6 14 0M7 14c2 3 8 3 10 0M12 6v12",
    [Slot.Ultimate]: "M12 3l2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5z",
    passive: "M7 19c-2-6 1-12 5-15c4 3 7 9 5 15M9 14h6",
  },
  audio: {
    weight: "mid",
    fire: (ctx) => ({ key: ctx?.alt ? "fire:atom-warlock:cluster-polish" : "fire:atom-warlock:ray-polish", cue: ctx?.alt ? atomCluster : atomRay }),
    reload: atomReload,
    ability: { [Slot.Ability2]: mendstream, [Slot.Ability1]: soulStitch, [Slot.Ultimate]: karmaReboot },
    theme: atomTheme,
    themeTrim: { heroic: 1.2, hostile: -6.2, tease: 0 },
    flight: { [PK.Cluster]: loops.flight("orb") },
    selfLift: ({ v, alive }) => (alive && v[V.flight] > 0 ? loops.levitate : undefined),
    constructLoops: { [CK.Cocoon]: { build: loops.portal, gain: 0.65, range: 30 } },
  },
};

void Btn;
export default hero;
